import { ReactNode, useEffect } from "react";
import { Flowbite, CustomFlowbiteTheme } from "flowbite-react";

const FIELD_SIZES = {
  sm: "p-2 text-base md:py-1.5 md:text-xs",
  md: "p-2.5 text-base md:py-2 md:text-sm",
  lg: "p-4 sm:text-base",
};

const FIELD_RADIUS = { on: "rounded-r-md", off: "rounded-md" };

const FIELD_GRAY =
  "border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500";

const inputZoomFixTheme: CustomFlowbiteTheme = {
  textInput: {
    field: {
      input: {
        sizes: FIELD_SIZES,
        withAddon: FIELD_RADIUS,
        colors: { gray: FIELD_GRAY },
      },
    },
  },
  select: {
    field: {
      select: {
        sizes: FIELD_SIZES,
        withAddon: FIELD_RADIUS,
        colors: { gray: FIELD_GRAY },
      },
    },
  },
  textarea: {
    base: "block w-full rounded-md border text-base md:text-sm disabled:cursor-not-allowed disabled:opacity-50",
    colors: { gray: FIELD_GRAY },
  },
  checkbox: {
    root: { color: { default: "text-blue-600 focus:ring-blue-600" } },
  },
  radio: {
    root: {
      base: "h-4 w-4 border border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-600",
    },
  },
  alert: {
    color: {
      info: "border-blue-200 bg-blue-50 text-blue-800",
      warning: "border-amber-200 bg-amber-50 text-amber-800",
      failure: "border-red-200 bg-red-50 text-red-800",
      success: "border-green-200 bg-green-50 text-green-800",
      gray: "border-gray-200 bg-gray-50 text-gray-700",
    },
    base: "flex flex-col gap-2 rounded-lg border p-4 text-sm",
  },
};

const isIOS = (): boolean =>
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const useIOSModalScrollLockFix = (): void => {
  useEffect(() => {
    if (!isIOS()) return;

    const body = document.body;
    const html = document.documentElement;

    let lockedScrollY: number | null = null;

    const applySafeLock = () => {
      if (lockedScrollY !== null) return;
      lockedScrollY = window.pageYOffset || html.scrollTop || 0;

      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";

      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    };

    const releaseSafeLock = () => {
      if (lockedScrollY === null) return;
      const y = lockedScrollY;
      lockedScrollY = null;

      html.style.overflow = "";
      body.style.overflow = "";
      window.scrollTo(0, y);
    };

    let rafId = 0;
    const observer = new MutationObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (body.style.position === "fixed") {
          if (lockedScrollY === null) applySafeLock();
          else {
            body.style.position = "";
            body.style.top = "";
            body.style.left = "";
            body.style.right = "";
            body.style.width = "";
            body.style.overflow = "hidden";
          }
        } else if (lockedScrollY !== null && body.style.overflow !== "hidden") {
          releaseSafeLock();
        }
      });
    });
    observer.observe(body, { attributes: true, attributeFilter: ["style"] });

    if (body.style.position === "fixed") applySafeLock();

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      releaseSafeLock();
    };
  }, []);
};

export const FlowbiteThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  useIOSModalScrollLockFix();
  return <Flowbite theme={{ theme: inputZoomFixTheme }}>{children}</Flowbite>;
};

export default FlowbiteThemeProvider;
